/**
 * Ticket Template System - Utilities
 *
 * Functions for getting and merging templates.
 */

import {
    TicketTemplate,
    TicketTemplateFields,
    TicketTemplateOverride,
    TicketContext,
    DEFAULT_FIELD,
} from './types'
import { getGroupTemplate } from './group-templates'
import { getKeyOverride } from './key-overrides'
import { getSampleContent } from './sample-content'

/**
 * Merge a base template with key-specific overrides
 */
function mergeTemplates(
    base: TicketTemplate,
    override: TicketTemplateOverride
): TicketTemplate {
    const mergedFields: TicketTemplateFields = {
        ...base.fields,
    }

    // Merge field overrides
    if (override.fields) {
        for (const fieldKey in override.fields) {
            if (
                Object.prototype.hasOwnProperty.call(override.fields, fieldKey)
            ) {
                const key = fieldKey as keyof TicketTemplateFields
                const fieldOverride = override.fields[key]
                if (fieldOverride) {
                    mergedFields[key] = {
                        ...mergedFields[key],
                        ...fieldOverride,
                    }
                }
            }
        }
    }

    return {
        ...base,
        ...override,
        fields: mergedFields,
        // Merge defaults
        defaults: {
            ...base.defaults,
            ...(override.defaults || {}),
        },
        // Merge validations
        validations: {
            ...base.validations,
            ...(override.validations || {}),
        },
    }
}

/**
 * Get the complete template for a ticket type
 *
 * @param group - Ticket type group
 * @param key - Optional ticket type key for specific overrides
 * @returns Complete merged template
 */
export function getTemplate(group: string, key?: string): TicketTemplate {
    // Get base template for group
    const baseTemplate = getGroupTemplate(group)

    if (!baseTemplate) {
        // Return a default template if group not found
        console.warn(`Template not found for group: ${group}`)
        return createDefaultTemplate(group)
    }

    // If no key specified, return base template
    if (!key) {
        return baseTemplate
    }

    // Get key-specific override
    const keyOverride = getKeyOverride(key)

    if (!keyOverride) {
        // No override, use sample content lookup
        return {
            ...baseTemplate,
            key,
            sampleContent: getSampleContent(group, key),
        }
    }

    // Merge base template with override
    const merged = mergeTemplates(baseTemplate, keyOverride)

    return {
        ...merged,
        key,
        // Ensure sample content is set (override or lookup)
        sampleContent:
            keyOverride.sampleContent || getSampleContent(group, key),
    }
}

/**
 * Create a default template for unknown groups
 */
function createDefaultTemplate(group: string): TicketTemplate {
    return {
        group: group as any,
        fields: {
            customer: { ...DEFAULT_FIELD, required: true },
            service: { ...DEFAULT_FIELD },
            content: { ...DEFAULT_FIELD, required: true },
            priority: { ...DEFAULT_FIELD },
            marjorable: { ...DEFAULT_FIELD, visible: false },
            affected: { ...DEFAULT_FIELD, visible: false },
            meeting: { ...DEFAULT_FIELD, visible: false },
            serviceDates: { ...DEFAULT_FIELD, visible: false },
            timeReply: { ...DEFAULT_FIELD },
            estimatedCompletion: { ...DEFAULT_FIELD },
            assignees: { ...DEFAULT_FIELD },
        },
        sampleContent: '',
        defaults: {},
        validations: {},
    }
}

/**
 * Check if a field is visible based on template and context
 *
 * @param template - The ticket template
 * @param fieldName - Name of the field to check
 * @param context - Current form context
 * @returns Whether the field should be visible
 */
export function isFieldVisible(
    template: TicketTemplate,
    fieldName: keyof TicketTemplateFields,
    context: TicketContext
): boolean {
    const field = template.fields[fieldName]

    if (!field) {
        return false
    }

    // If field has a condition function, use it
    if (field.condition) {
        return field.condition(context)
    }

    // Otherwise use static visibility
    return field.visible
}

/**
 * Check if a field is required based on template
 *
 * @param template - The ticket template
 * @param fieldName - Name of the field to check
 * @returns Whether the field is required
 */
export function isFieldRequired(
    template: TicketTemplate,
    fieldName: keyof TicketTemplateFields
): boolean {
    const field = template.fields[fieldName]
    return field?.required === true
}

/**
 * Check if a field is disabled based on template
 *
 * @param template - The ticket template
 * @param fieldName - Name of the field to check
 * @returns Whether the field is disabled
 */
export function isFieldDisabled(
    template: TicketTemplate,
    fieldName: keyof TicketTemplateFields
): boolean {
    const field = template.fields[fieldName]
    return field?.disabled === true
}

/**
 * Get default values from template
 *
 * @param template - The ticket template
 * @returns Default values object
 */
export function getTemplateDefaults(
    template: TicketTemplate
): Record<string, unknown> {
    return template.defaults
}

/**
 * Get service date variant from template
 *
 * @param template - The ticket template
 * @returns Service date variant or 'default'
 */
export function getServiceDateVariant(template: TicketTemplate): string {
    return template.fields.serviceDates?.variant || 'default'
}
